import * as React from "react";

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  /**
   * Slim breadcrumb above the title — each crumb gets a dim slash separator
   * (Linear / Stripe Dashboard convention). Single-element `trail` arrays
   * still render so users always have an "I'm in the admin app" anchor.
   */
  trail?: string[];
}

/**
 * Spec:
 *
 * - title 24/600
 * - description 13/400 muted
 * - 1px divider below the row, then 24px space before content
 * - actions slot is right-aligned and shrink-0
 */
export const PageHeader = ({ title, description, actions, trail }: PageHeaderProps) => (
  <div className="mb-5 flex flex-col gap-3 border-b border-border pb-4 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:pb-5">
    <div className="min-w-0">
      {trail && trail.length > 0 && (
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
          {trail.map((crumb, idx) => (
            <span key={crumb}>
              {crumb}
              {idx < trail.length - 1 ? <span className="px-2 opacity-50">/</span> : null}
            </span>
          ))}
        </p>
      )}
      <h1 className="text-[20px] font-semibold leading-tight tracking-tight text-foreground sm:text-[24px]">
        {title}
      </h1>
      {description && <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>}
    </div>
    {actions && (
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap">{actions}</div>
    )}
  </div>
);
